import { formatNumber } from "@angular/common";
import { Component, ElementRef, HostListener, Input, OnInit, ViewChild } from "@angular/core";
import { DomSanitizer } from "@angular/platform-browser";
import { ActivatedRoute, Router } from "@angular/router";
import { Globals } from "@app/_helpers";
import { Accounts, Conclusions, Files, Kpi, TextSlides, Totals } from "@app/_models";
import { AuthenticationService } from "@app/_services";
import { ApiBackendService } from "@app/_services/api-backend.service";
import html2canvas from "html2canvas";
import jsPDF from "jspdf";
import { Observable } from "rxjs";
import { environment } from "../../../../environments/environment";
import { AppComponent } from "@app/app.component";
import { TranslateService } from "@ngx-translate/core";

@Component({
  selector: "app-file-compare",
  templateUrl: "./file-compare.component.html",
  styleUrls: ["./file-compare.component.css"],
})
export class FileCompareComponent implements OnInit {
  apiUrl = null;
  totals: Observable<Totals[]>;
  accounts: Observable<Accounts[]>;
  kpis: Observable<Kpi[]>;
  conclusions: Observable<Conclusions[]>;
  slides: Observable<TextSlides[]>;
  public account: any = [];
  public accountsCompare: any = {}; // Accounts data for each period, keyed by description
  public groupsCompare: any = {}; // Group values for each period, keyed by group id
  public total: any = [];
  public totalsMeta: any = {}; // Metadata for expandable rows (accountTypeId, id, group)
  public kpi: any = [];
  kpiResults = {};
  public allKpi: any = [];
  public conclusion: any = [];
  public textSlides: any = [];
  loadingAccounts = true;
  loadingTotals = true;
  loadingKpi = true;
  loadingConclusions = true;
  loadingSlides = true;
  loadingExport = true;
  loadingDownload = false;
  expandSet = new Set<number>();
  columnNames: any = [];
  columnNames2: any = [];
  chartConclusionsColNames: any = [];
  userRole = null;
  c = [];
  excludeFromCompare = [1, 2, 3, 4, 5, 6, 7, 18, 20, 23, 28, 31, 33, 34, 36, 39, 40, 41, 42];
  months = ["Ian", "Feb", "Mar", "Apr", "Mai", "Iun", "Iul", "Aug", "Sep", "Oct", "Nov", "Dec"];
  diff = [];
  private completedTotalsCount = 0;
  private completedKpisCount = 0;
  private completedConclusionsCount = 0;
  private textSlidesLoaded = false;
  private diffsCalculated = false;
  colspan = 0;

  counter: number;
  length: number;
  pdf: jsPDF;
  chartsUrl = environment.frontendUrl + "/charts";

  // c2 al doilea grafic       'Evolutie Credite & Leasing': [],
  // suma de Datorii pe termen scurt + Datorii pe termen lung
  charts = {
    c1: {
      "Evolutie Venituri Operationale": [],
      "Evolutie EBITDA": [],
      "Evolutie Profit Net": [],
      "Evolutie Cheltuieli cu Materiale": [],
      "Evolutie Cheltuieli cu Serviciile": [],
      "Evolutie Cheltuieli cu Salarii": [],
      "Evolutie Cheltuieli Financiare (dobanzi, comisioane, curs valutar)": [],
    },
    c2: {
      "Evolutie Patrimoniu & Surse de finantare": [],
      "Evolutie Active Fixe (terenuri, cladiri, utilaje, licente, marci, garantii clienti)": [],
      "Evolutie Stocuri": [],
      "Evolutie Creante": [],
      "Evolutie Cash": [],
      "Evolutie Datorii Curente": [],
      "Evolutie Datorii pe termen lung": [],
      "Evolutie CAPITAL PROPRIU - sursa proprie de finantare": [],
    },
    c3: {
      ch1: {
        title: ["Venituri, EBITDA, Net Profit "],
        columnNames: [" ", "Venituri operationale", { role: "annotation" }, "EBITDA", { role: "annotation" }, "Profit net", { role: "annotation" }],
        data: [],
      },
      ch2: {
        title: ["Venituri, EBITDA, Net Profit"],
        columnNames: [],
        data: [["Venituri operationale"], ["EBITDA"], ["Profit net"]],
      },
    },
  };
  // ch1 = {'title': [], 'columns': [], 'data': []};
  sysLanguage = "ro";
  isLanguageSelectorVisible = true;

  constructor(private authenticationService: AuthenticationService, private router: Router, private activatedRoute: ActivatedRoute, private sanitizer: DomSanitizer, private el: ElementRef, public _api: ApiBackendService, private globals: Globals, private app: AppComponent, private translateService: TranslateService) {
    // redirect to home if not logged
    if (!this.authenticationService.currentUserValue) {
      this.router.navigate(["/login"]);
    }
    this.userRole = this.authenticationService.currentUserInfo.role;
    this.apiUrl = this.authenticationService.apiUrl;
  }

  onExpandChange(id: number, checked: boolean): void {
    if (checked) {
      this.expandSet.add(id);
    } else {
      this.expandSet.delete(id);
    }
  }

  isNum(value) {
    if (Number(value)) {
      return true;
    }
    return false;
  }

  // Check if a row is expandable based on its metadata (has group or sub-accounts)
  isExpandable(tableName: string, key: string): boolean {
    if (!this.totalsMeta[tableName] || !this.totalsMeta[tableName][key]) {
      return false;
    }
    const meta = this.totalsMeta[tableName][key];

    // Check if has group data
    if (meta.group && meta.group.length > 0) {
      return true;
    }

    // Check if has accountTypeId with sub-accounts
    const accountTypeId = meta.accountTypeId;
    if (!accountTypeId) {
      return false;
    }
    const hasSubAccounts = this.accountsCompare[accountTypeId] || this.accountsCompare[String(accountTypeId)];
    return !!hasSubAccounts && Object.keys(hasSubAccounts).length > 0;
  }

  // Check if a row has group data (not direct accounts)
  hasGroup(tableName: string, key: string): boolean {
    if (!this.totalsMeta[tableName] || !this.totalsMeta[tableName][key]) {
      return false;
    }
    const meta = this.totalsMeta[tableName][key];
    return meta.group && meta.group.length > 0;
  }

  // Get group data for a row
  getGroup(tableName: string, key: string): any[] {
    if (!this.totalsMeta[tableName] || !this.totalsMeta[tableName][key]) {
      return [];
    }
    return this.totalsMeta[tableName][key].group || [];
  }

  // Get group values with calculated diffs for a specific group id
  // Values are calculated as SUM of all sub-accounts under this group
  getGroupValues(groupId: number): any[] {
    const groupData = this.groupsCompare[groupId];
    if (!groupData) {
      return [];
    }

    const numPeriods = this.c.length;

    // If we have an accountTypeId, calculate values from sub-accounts
    if (groupData.accountTypeId) {
      const subAccounts = this.accountsCompare[groupData.accountTypeId] || this.accountsCompare[String(groupData.accountTypeId)] || {};

      // Check if we already calculated the summed values
      if (!groupData.calculatedValues) {
        groupData.calculatedValues = [];

        // Sum up all sub-account values for each period
        for (let p = 0; p < numPeriods; p++) {
          let sum = 0;
          for (const desc of Object.keys(subAccounts)) {
            const val = subAccounts[desc][p];
            if (val !== undefined && !isNaN(Number(val))) {
              sum += Number(val);
            }
          }
          groupData.calculatedValues[p] = sum;
        }

        // Calculate diffs
        for (let j = 0; j < numPeriods - 1; j++) {
          const valCurrent = groupData.calculatedValues[j] || 0;
          const valNext = groupData.calculatedValues[j + 1] || 0;
          groupData.calculatedValues[numPeriods + j] = valNext - valCurrent;
        }
      }

      return groupData.calculatedValues;
    }

    // Fallback to stored values if no accountTypeId
    const values = groupData.values || [];

    // Calculate diffs if not already done
    if (values[numPeriods] === undefined && numPeriods > 1) {
      for (let j = 0; j < numPeriods - 1; j++) {
        let valCurrent = values[j];
        if (valCurrent == undefined) {
          valCurrent = 0;
        }
        let valNext = values[j + 1];
        if (valNext == undefined) {
          valNext = 0;
        }
        const diffValue = Number(valNext) - Number(valCurrent);
        values[numPeriods + j] = diffValue;
      }
    }
    return values;
  }

  // Get the unique ID for a row (used for expandSet)
  getRowId(tableName: string, key: string): number {
    if (this.totalsMeta[tableName] && this.totalsMeta[tableName][key]) {
      return this.totalsMeta[tableName][key].id || 0;
    }
    return 0;
  }

  // Get accountTypeId for a row
  getAccountTypeId(tableName: string, key: string): any {
    if (this.totalsMeta[tableName] && this.totalsMeta[tableName][key]) {
      return this.totalsMeta[tableName][key].accountTypeId;
    }
    return 0;
  }

  // Get sub-accounts for a given accountTypeId
  getSubAccounts(accountTypeId: any): any {
    return this.accountsCompare[accountTypeId] || this.accountsCompare[String(accountTypeId)] || {};
  }

  // Convert accounts object to array for iteration (with lazy diff calculation)
  getSubAccountsArray(accountTypeId: any): any[] {
    // Try both number and string keys to handle potential type mismatches
    const accounts = this.accountsCompare[accountTypeId] || this.accountsCompare[String(accountTypeId)] || {};
    const numPeriods = this.c.length;
    return Object.keys(accounts).map(desc => {
      const values = accounts[desc] || [];
      // Calculate diffs lazily if not already calculated
      // Period values are at indices 0 to c.length-1, diffs should start at c.length
      // Check if diffs already calculated by checking if value at index numPeriods exists
      if (values[numPeriods] === undefined) {
        // Diffs not yet calculated, calculate now
        for (let j = 0; j < numPeriods - 1; j++) {
          let valCurrent = values[j];
          if (valCurrent == undefined) {
            valCurrent = 0;
          }
          let valNext = values[j + 1];
          if (valNext == undefined) {
            valNext = 0;
          }
          const diffValue = Number(valNext) - Number(valCurrent);
          // Set at specific index to ensure correct position
          values[numPeriods + j] = diffValue;
        }
      }
      return {
        description: desc,
        values: values
      };
    });
  }

  // Check if all data is loaded and calculate diffs if ready
  private tryCalculateDiffs(): void {
    // Only calculate once, and only when ALL data sources are loaded
    if (this.diffsCalculated) {
      return;
    }
    if (this.completedTotalsCount < this.c.length) {
      return;
    }
    if (this.completedKpisCount < this.c.length) {
      return;
    }
    if (this.completedConclusionsCount < this.c.length) {
      return;
    }
    if (!this.textSlidesLoaded || this.textSlides.length === 0) {
      return;
    }

    this.diffsCalculated = true;
    this.calculateDiffs();
  }

  // Calculate diff values for all tables
  private calculateDiffs(): void {
    let translationPromises = [];
    const numPeriods = this.c.length;

    // First, build diff labels (only if not already built)
    if (this.diff.length === 0) {
      for (let i = 0; i < numPeriods - 1; i++) {
        const diffLabel = this.months[this.c[i + 1].month - 1] + " " + this.c[i + 1].year.toString().substr(this.c[i + 1].year.toString().length - 2) + "\n- " + this.months[this.c[i].month - 1] + " " + this.c[i].year.toString().substr(this.c[i].year.toString().length - 2);
        this.diff.push(diffLabel);
      }
    }

    // Track which data objects have been processed to avoid double calculation
    // (multiple slides may reference the same data object)
    const processedDataObjects = new Set<any>();

    // Then, calculate diff values for each unique table data object
    for (let o of this.textSlides) {
      if (o.tableData != undefined && typeof o.tableData === 'object') {
        // Skip if this data object was already processed
        if (processedDataObjects.has(o.tableData)) {
          continue;
        }
        processedDataObjects.add(o.tableData);

        for (const [key, value] of Object.entries(o.tableData)) {
          // Skip if not an array
          if (!Array.isArray(value)) {
            continue;
          }

          // Skip header rows (empty arrays or arrays that don't have period data)
          // A valid data array should have at least some values in the period indices
          let hasValidData = false;
          for (let p = 0; p < numPeriods; p++) {
            if (value[p] !== undefined) {
              hasValidData = true;
              break;
            }
          }
          if (!hasValidData) {
            continue;
          }

          // Calculate diffs for each consecutive pair of periods
          // Set at specific indices (numPeriods + i) instead of pushing to handle arrays with extra values
          for (let i = 0; i < numPeriods - 1; i++) {
            let diffValue;
            let vali = value[i];
            if (vali == undefined) {
              vali = 0;
            }
            let vali1 = value[i + 1];
            if (vali1 == undefined) {
              vali1 = 0;
            }
            if (key.startsWith("Marja") || key == "%") {
              const vali1Str = String(vali1).replace("%", "");
              const valiStr = String(vali).replace("%", "");
              diffValue = (parseFloat(vali1Str) - parseFloat(valiStr)).toFixed(2) + "%";
            } else {
              diffValue = Number(vali1) - Number(vali);
            }
            // Set at specific index to ensure diffs are at correct position
            value[numPeriods + i] = diffValue;
          }
        }
      }
    }

    // Save charts logic - separate loop to avoid interference
    for (let i = 0; i < numPeriods; i++) {
      for (let o of this.textSlides) {
        if (o.tableData != undefined && typeof o.tableData === 'object') {
          for (const [key, value] of Object.entries(o.tableData)) {
            if (!Array.isArray(value)) {
              continue;
            }
            if (key.startsWith("account")) {
              translationPromises.push(
                this.translateKey(key).then((translatedKey: string) => {
                  let vali = value[i];
                  if (vali == undefined) {
                    vali = "0";
                  }
                  this.saveCharts(translatedKey, o.id, this.months[this.c[i].month - 1] + " " + this.c[i].year, vali);
                })
              );
            } else {
              let vali = value[i];
              if (vali == undefined) {
                vali = "0";
              }
              this.saveCharts(key, o.id, this.months[this.c[i].month - 1] + " " + this.c[i].year, vali);
            }
          }
        }
      }
    }

    this.loadingSlides = false;
    this.loadingKpi = false;
    Promise.all(translationPromises).then(() => {
      localStorage.setItem("charts", JSON.stringify(this.charts));
    });
  }

  cleanDesc(desc) {
    if (desc.startsWith("EBITDA") || desc.startsWith("EBIT")) {
      desc = desc.split(" ")[0];
    }
    if (desc.startsWith("TOTAL ACTIV") || desc.startsWith("TOTAL PASIV") || desc.startsWith("Capital propriu (")) {
      desc = desc.split(" ")[0] + " " + desc.split(" ")[1];
    }
    return desc;
  }

  ngOnInit() {
    this.app.switchLanguage(this.sysLanguage);

    // Try globals first, then localStorage
    let compareData = this.globals.checkCompare;
    console.log('globals.checkCompare:', compareData);

    if (compareData == undefined || compareData.length === 0) {
      // Try to retrieve from localStorage
      const storedData = localStorage.getItem("checkCompare");
      console.log('localStorage checkCompare:', storedData);
      if (storedData) {
        try {
          compareData = JSON.parse(storedData);
          this.globals.checkCompare = compareData; // Restore to globals
          console.log('Restored from localStorage:', compareData);
        } catch (e) {
          console.error('Error parsing localStorage:', e);
        }
      }
    }

    if (compareData == undefined || compareData.length === 0) {
      console.log('No compare data found, redirecting to files');
      this.router.navigate(["/files"]);
      return; // Stop execution if no comparison data
    }
    this.c = compareData;
    // console.log('comp', this.globals.checkCompare);
    // this.c = [
    //   {
    //     IDBalanceProcessed: 1941,
    //     month: 4,
    //     year: 2022,
    //   },
    //   {
    //     IDBalanceProcessed: 1942,
    //     month: 6,
    //     year: 2022,
    //   },
    //   {
    //     id: 1940,
    //     month: 6,
    //     year: 2022,
    //   },
    //   {
    //     id: 1941,
    //     month: 4,
    //     year: 2022,
    //   },
    //   {
    //     id: 1942,
    //     month: 6,
    //     year: 2022,
    //   },
    //   {
    //     id: 1940,
    //     month: 6,
    //     year: 2022,
    //   },
    // ];
    window.scrollTo(0, 0);

    this.total["t1"] = {};
    this.total["t2"] = {};
    this.totalsMeta["t1"] = {};
    this.totalsMeta["t2"] = {};
    this.accountsCompare = {};
    this.groupsCompare = {};
    this.kpi = {};
    this.conclusion["concluzii1"] = {};
    this.conclusion["concluzii1p"] = {};
    this.conclusion["concluzii2"] = {};
    this.conclusion["MarjaBrutaTabel"] = {};
    // Reset tracking variables for diff calculation
    this.completedTotalsCount = 0;
    this.completedKpisCount = 0;
    this.completedConclusionsCount = 0;
    this.textSlidesLoaded = false;
    this.diffsCalculated = false;
    this.diff = [];
    let desc = "";

    // Load textSlides once (not per period) - use first processId
    const firstProcessId = this.c[0].IDBalanceProcessed;
    this._api
      .getTextSlidesByLang(firstProcessId, this.sysLanguage)
      .pipe()
      .subscribe(
        (res) => {
          let textSlides = [];
          for (let o of res.body) {
            let td = "";
            if (o.tableName == "bp_totals1") {
              td = this.total["t1"];
            }
            if (o.tableName == "bp_totals2") {
              td = this.total["t2"];
              o.tableName = "t2";
            }
            if (o.tableName == "MarjaBrutaTabel") {
              td = this.conclusion["MarjaBrutaTabel"];
            }
            if (o.tableName == "concluzii1") {
              td = this.conclusion["concluzii1"];
            }
            if (o.tableName == "concluzii2") {
              td = this.conclusion["concluzii2"];
            }
            if (o.tableName == "kpi") {
              td = this.kpi;
              o.tableName = "kpi";
            }
            o.tableData = td;
            textSlides.push(o);
          }

          for (let o of textSlides) {
            if (o["accounts"] != undefined && o["accounts"].length > 0) {
              for (let i = 0; i < o["accounts"].length; i++) {
                let description = o["accounts"][i]["account_description"];
                if (description && typeof description === "string" && description.startsWith("account")) {
                  this.translateKey(description).then((translatedKey: string) => {
                    o["accounts"][i]["account_description"] = translatedKey;
                  });
                }
              }
            }
          }

          this.textSlides = textSlides;
          this.textSlidesLoaded = true;
          this.loadingExport = false;
          // Try to calculate diffs if totals are also ready
          this.tryCalculateDiffs();
        },
        (err) => {}
      );

    for (let i = 0; i < this.c.length; i++) {
      let processId = this.c[i].IDBalanceProcessed;

      this._api
        .getTotals(processId)
        .pipe()
        .subscribe(
          (res) => {
            for (const o of res.body[0]["t1"].totals) {
              if (o.description.startsWith("account")) {
                this.translateKey(o.description).then((translatedKey: string) => {
                  o.description = translatedKey;
                });
              }
              const descKey = this.cleanDesc(o.description);
              if (this.total["t1"][descKey] == undefined) {
                this.total["t1"][descKey] = [];
              }
              this.total["t1"][descKey][i] = o.value;
              // Store metadata for expandable rows (any period can set the metadata)
              // Store if accountTypeId OR group exists
              if ((o.accountTypeId || o.group) && !this.totalsMeta["t1"][descKey]) {
                this.totalsMeta["t1"][descKey] = {
                  id: o.id,
                  accountTypeId: o.accountTypeId || null,
                  group: o.group || null
                };
              }
              // Store group values for each period
              if (o.group && Array.isArray(o.group)) {
                for (const g of o.group) {
                  if (g.id) {
                    if (!this.groupsCompare[g.id]) {
                      this.groupsCompare[g.id] = { description: g.description, accountTypeId: g.accountTypeId, values: [] };
                    }
                    this.groupsCompare[g.id].values[i] = g.value;
                  }
                }
              }
            }
            for (const o of res.body[1]["t2"].totals) {
              if (o.description == "%") {
                desc = "Marja " + desc;
              } else {
                if (o.description.startsWith("account")) {
                  this.translateKey(o.description).then((translatedKey: string) => {
                    o.description = translatedKey;
                  });
                }
                desc = this.cleanDesc(o.description);
              }
              if (this.total["t2"][desc] == undefined) {
                this.total["t2"][desc] = [];
              }
              if (o.description == "%") {
                try {
                  o.value = Number.parseFloat(o.value) + "%";
                } catch {
                  o.value = 0;
                }
              }
              this.total["t2"][desc][i] = o.value;
              // Store metadata for expandable rows (any period can set the metadata)
              // Store if accountTypeId OR group exists
              if ((o.accountTypeId || o.group) && !this.totalsMeta["t2"][desc]) {
                this.totalsMeta["t2"][desc] = {
                  id: o.id,
                  accountTypeId: o.accountTypeId || null,
                  group: o.group || null
                };
              }
              // Store group values for each period
              if (o.group && Array.isArray(o.group)) {
                for (const g of o.group) {
                  if (g.id) {
                    if (!this.groupsCompare[g.id]) {
                      this.groupsCompare[g.id] = { description: g.description, accountTypeId: g.accountTypeId, values: [] };
                    }
                    this.groupsCompare[g.id].values[i] = g.value;
                  }
                }
              }
            }
            this.loadingTotals = false;
            // Track completion and try to calculate diffs
            this.completedTotalsCount++;
            this.tryCalculateDiffs();
          },
          (err) => {
            this.router.navigate(["/files"]);
          }
        );

      this._api
        .getKpis(processId)
        .pipe()
        .subscribe(
          (res) => {
            let kpi = [];
            let kpiType = "";
            for (const o of res.body) {
              if (kpiType != o["kpiType"]) {
                this.kpi[o["kpiType"]] = [];
                this.kpiResults[o["kpiType"]] = "";
              }
              if (this.kpi[o["kpiDescription"]] == undefined) {
                this.kpi[o["kpiDescription"]] = [];
              }
              this.kpi[o["kpiDescription"]][i] = o["value"];
              this.kpiResults[o["value"]] = o["kpiResult"];
              // this.kpi[o['kpiDescription']][o['value']] = o['kpiResult'];
            }
            // this.kpi = kpi;
            this.loadingKpi = false;
            // Track completion and try to calculate diffs
            this.completedKpisCount++;
            this.tryCalculateDiffs();
            // console.log('kpi', this.kpi)
          },
          (err) => {
            // Still count as completed even on error to avoid blocking
            this.completedKpisCount++;
            this.tryCalculateDiffs();
          }
        );

      this._api
        .getConclusions(processId)
        .pipe()
        .subscribe(
          (res) => {
            for (const o of res.body[0]["concluzii1"]) {
              if (this.conclusion["concluzii1"][o[0]] == undefined) {
                this.conclusion["concluzii1"][o[0]] = [];
              }
              this.conclusion["concluzii1"][o[0]][i] = o[1];
              if (this.conclusion["concluzii1p"][o[0]] == undefined) {
                this.conclusion["concluzii1p"][o[0]] = [];
              }
              this.conclusion["concluzii1p"][o[0]][i] = o[2];
            }
            for (const o of res.body[1]["concluzii2"]) {
              if (this.conclusion["concluzii2"][o[0]] == undefined) {
                this.conclusion["concluzii2"][o[0]] = [];
              }
              this.conclusion["concluzii2"][o[0]][i] = o[1];
            }
            for (const o of res.body[4]["MarjaBrutaTabel"]) {
              if (this.conclusion["MarjaBrutaTabel"][this.cleanDesc(o[1])] == undefined) {
                this.conclusion["MarjaBrutaTabel"][this.cleanDesc(o[1])] = [];
              }
              if (o[1] == "%") {
                try {
                  o[2] = Number.parseFloat(o[2]).toFixed(2) + "%";
                } catch {
                  o[2] = 0;
                }
              }
              this.conclusion["MarjaBrutaTabel"][this.cleanDesc(o[1])][i] = o[2];
            }
            for (const [key, value] of Object.entries(this.conclusion["MarjaBrutaTabel"])) {
              if (this.conclusion["MarjaBrutaTabel"][key].length < this.c.length) {
                this.conclusion["MarjaBrutaTabel"][key].push(0);
              }
            }
            // for(const k of this.conclusion['MarjaBrutaTabel']){
            //   console.log('k', k)
            // }
            this.loadingConclusions = false;
            // Track completion and try to calculate diffs
            this.completedConclusionsCount++;
            this.tryCalculateDiffs();
          },
          (err) => {
            // Still count as completed even on error to avoid blocking
            this.completedConclusionsCount++;
            this.tryCalculateDiffs();
          }
        );

      // Load accounts for expandable rows
      this._api
        .getAccounts(processId)
        .pipe()
        .subscribe(
          (res) => {
            // Store accounts indexed by accountTypeId for this period
            for (const acc of res.body) {
              const accTypeId = acc.accountTypeId;
              if (accTypeId) {
                if (!this.accountsCompare[accTypeId]) {
                  this.accountsCompare[accTypeId] = {};
                }
                if (!this.accountsCompare[accTypeId][acc.accountDescription]) {
                  this.accountsCompare[accTypeId][acc.accountDescription] = [];
                }
                this.accountsCompare[accTypeId][acc.accountDescription][i] = acc.value;
              }
            }
            // Also store flat account array for first period (for backward compatibility)
            if (i === 0) {
              this.account = res.body;
            }
            this.loadingAccounts = false;
          },
          (err) => {
            this.loadingAccounts = false;
          }
        );
    }

    // Note: Diff calculation is now handled by tryCalculateDiffs() when all data is loaded

    this._api
      .getAllKpis()
      .pipe()
      .subscribe(
        (res) => {
          this.allKpi = res.body;
        },
        (err) => {}
      );
  }

  translateKey(key: string): Promise<string> {
    return new Promise((resolve) => {
      this.translateService.get(key).subscribe((translated: string) => {
        resolve(translated);
      });
    });
  }

  getTranslationForKey(lang: string, key: string): string {
    let translatedKey: string;
    this.translateService.get(key).subscribe((translation) => {
      translatedKey = translation || key;
    });
    return translatedKey;
  }

  saveCharts(key, id, label, value) {
    const translatedKey = this.getTranslationForKey("ro", key);

    let lengthData = this.charts["c3"]["ch2"]["data"].length;
    if (this.charts["c3"]["ch2"]["columnNames"].length == 0) {
      this.charts["c3"]["ch2"]["columnNames"].push(" ");
    }

    if (translatedKey == "Venituri operationale") {
      // this.charts['c3']['ch1']['title'].push(label);
      // this.charts['c3']['ch2']['title'].push(label);
      this.charts["c3"]["ch2"]["columnNames"].push(label);
      this.charts["c3"]["ch2"]["columnNames"].push({ role: "annotation" });
      // this.charts['c3']['ch2']['columnNames'].push({ type: 'number', role: 'annotation'});
      // if (this.charts['c3']['ch2']['data'][lengthData] == undefined) {
      //   console.log(label, value, lengthData);
      //   this.charts['c3']['ch2']['data'].push([label]);
      // }
      // if (this.charts['c3']['ch1']['data'][lengthData] == undefined) {
      //   this.charts['c3']['ch1']['data'].push([label]);
      // }

      // this.charts['c3']['ch1']['data'][lengthData].push(value);
      // this.charts['c3']['ch1']['data'][lengthData].push('Venituri');
      this.charts["c3"]["ch2"]["data"][0].push(value);
      this.charts["c3"]["ch2"]["data"][0].push(label);
      this.charts["c1"]["Evolutie Venituri Operationale"].push([label, value, value]);
    }
    lengthData = this.charts["c3"]["ch2"]["data"].length - 1;
    lengthData = lengthData == -1 ? 0 : lengthData;

    if (translatedKey == "EBITDA") {
      // if (this.charts['c3']['ch1']['data'][lengthData] == undefined) {
      //   this.charts['c3']['ch1']['data'].push([label]);
      // }
      // if (!this.charts['c3']['ch1']['data'][lengthData].includes(value)) {
      //   this.charts['c3']['ch1']['data'][lengthData].push(value);
      //   this.charts['c3']['ch1']['data'][lengthData].push('EBITDA');
      // }

      let isAlreadyAdded = this.charts["c1"]["Evolutie EBITDA"].some((item) => item[0] === label && item[1] === value);

      if (!isAlreadyAdded) {
        this.charts["c1"]["Evolutie EBITDA"].push([label, value, value]);
      }
      if (this.sysLanguage == "ro") {
        if (this.charts["c3"]["ch2"]["data"][1].length <= this.charts["c3"]["ch2"]["data"][2].length) {
          this.charts["c3"]["ch2"]["data"][1].push(value);
          this.charts["c3"]["ch2"]["data"][1].push(label);
        }
      } else {
        if (this.charts["c3"]["ch2"]["data"][1].length < this.charts["c3"]["ch2"]["data"][2].length) {
          this.charts["c3"]["ch2"]["data"][1].push(value);
          this.charts["c3"]["ch2"]["data"][1].push(label);
        }
      }
    }
    if (translatedKey == "Profit net" && id == 24) {
      // this.charts['c3']['ch1']['data'][lengthData].push(value);
      // this.charts['c3']['ch1']['data'][lengthData].push('Profit net');
      this.charts["c3"]["ch2"]["data"][2].push(value);
      this.charts["c3"]["ch2"]["data"][2].push(label);
      this.charts["c1"]["Evolutie Profit Net"].push([label, value, value]);
    }
    if (translatedKey == "Cheltuieli materiale") {
      this.charts["c1"]["Evolutie Cheltuieli cu Materiale"].push([label, value, value]);
    }
    if (translatedKey == "Cheltuieli cu serviciile") {
      this.charts["c1"]["Evolutie Cheltuieli cu Serviciile"].push([label, value, value]);
    }
    if (translatedKey == "Cheltuieli cu salariile") {
      this.charts["c1"]["Evolutie Cheltuieli cu Salarii"].push([label, value, value]);
    }
    if (translatedKey == "Cheltuieli financiare") {
      this.charts["c1"]["Evolutie Cheltuieli Financiare (dobanzi, comisioane, curs valutar)"].push([label, value, value]);
    }
    if (translatedKey == "TOTAL ACTIV") {
      this.charts["c2"]["Evolutie Patrimoniu & Surse de finantare"].push([label, value, value]);
    }
    /// trebuie corectata coloana, e greseala in excel
    // if (translatedKey == 'Active Fixe') {
    //   this.charts['c2']['Evolutie Credite & Leasing'].push([
    //     label,
    //     value,
    //     value,
    //   ]);
    // }
    if (translatedKey == "Active Fixe") {
      this.charts["c2"]["Evolutie Active Fixe (terenuri, cladiri, utilaje, licente, marci, garantii clienti)"].push([label, value, value]);
    }
    if (translatedKey == "Stocuri") {
      this.charts["c2"]["Evolutie Stocuri"].push([label, value, value]);
    }
    if (translatedKey == "Creante de incasat") {
      this.charts["c2"]["Evolutie Creante"].push([label, value, value]);
    }
    if (translatedKey == "Cash") {
      this.charts["c2"]["Evolutie Cash"].push([label, value, value]);
    }
    if (translatedKey == "Datorii pe termen scurt") {
      this.charts["c2"]["Evolutie Datorii Curente"].push([label, value, value]);
    }
    if (translatedKey == "Datorii pe termen lung") {
      this.charts["c2"]["Evolutie Datorii pe termen lung"].push([label, value, value]);
    }
    if (translatedKey == "Capital propriu (sursa proprie de finanțare a patrimoniului companiei)") {
      this.charts["c2"]["Evolutie CAPITAL PROPRIU - sursa proprie de finantare"].push([label, value, value]);
    }
  }

  ngOnDestroy() {
    this.columnNames;
    this.columnNames2;
    this.textSlides;
    this.total;
    this.account;
    this.kpi;
    this.columnNames;
    this.allKpi;
    this.conclusion;
  }

  public convert(value) {
    return this.sanitizer.bypassSecurityTrustHtml(value);
  }

  public notesConvert(value, id) {
    let countStrings = value.split(" ").length;
    let strings = this.sanitizer.bypassSecurityTrustHtml(value);
    return strings;
  }

  public htmltoPDF() {
    this.loadingDownload = true;
    this.pdf = new jsPDF("p", "mm", "a4");
    this.length = this.textSlides.length;
    // this.length = 3
    this.counter = 0;

    this.generatePDF();
  }

  generatePDF() {
    let data = document.getElementById("slide_" + this.counter);

    html2canvas(data, {
      // scale: 3
    }).then((canvas) => {
      this.counter++;

      var imgWidth = 208;
      var imgHeight = (canvas.height * imgWidth) / canvas.width;

      // console.log(data);

      const contentDataURL = canvas.toDataURL("image/png");
      var position = 0;
      this.pdf.addImage(contentDataURL, "PNG", 0, position, imgWidth, imgHeight);

      if (this.counter < this.length) {
        // console.log('addPage');
        this.pdf.addPage();
        this.generatePDF();
      } else {
        // console.log('save');
        this.loadingDownload = false;
        this.pdf.save("test.pdf");
        return true;
      }
    });
  }

  public htmltohtml() {
    console.log(this.el.nativeElement.querySelector("#topHead").outerHTML.replace(/<!--[\s\S]*?-->/g, ""));
  }

  goToAnchor(id) {
    document.querySelector("#" + id).scrollIntoView();
  }

  loadCharts() {
    this.router.navigate(["/charts"]);
  }
  @HostListener("window:resize", ["$event"])
  onWindowResize(event: any) {}

  public keepOriginalOrder = (a, b) => a.key;
  isNumber(val): boolean {
    return typeof val === "number";
  }

  public switchLanguage(language: string) {
    this.app.switchLanguage(language);
    this.sysLanguage = language;
    this.resetData();
    this.ngOnInit();
  }

  resetData(): void {
    this.total = { t1: {}, t2: {} };
    this.totalsMeta = { t1: {}, t2: {} };
    this.accountsCompare = {};
    this.groupsCompare = {};
    this.account = [];
    this.expandSet.clear();
    this.kpi = {};
    this.conclusion = {
      concluzii1: {},
      concluzii1p: {},
      concluzii2: {},
      MarjaBrutaTabel: {},
    };
    this.textSlides = [];
    this.diff = [];
    this.completedTotalsCount = 0;
    this.completedKpisCount = 0;
    this.completedConclusionsCount = 0;
    this.textSlidesLoaded = false;
    this.diffsCalculated = false;
    this.loadingAccounts = true;
    this.loadingTotals = true;
    this.loadingKpi = true;
    this.loadingConclusions = true;
    this.loadingSlides = true;
    this.loadingExport = true;
  }
  isValueInList<T>(value: T, list: T[]): boolean {
    return list.includes(value);
  }
}
